import React from 'react'
import { observer } from 'mobx-react'

import Switch from '../../../UniformComponents/Switch'
import Gate from './Gate'
import Combinator from './Combinator'
import Savebar from './Savebar'

const ScopeOption = ({ option, active, onClick }) => (
    <div
        className={`ff-scope-option ${active ? 'is-selected' : ''}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
    >
        <span className="ff-scope-option__label">{option.label}</span>
        <span className="ff-scope-option__hint">{option.hint}</span>
    </div>
)

const EditView = ({ store }) => {
    const { flag } = store
    if (!flag) return null

    return (
        <div className="ff-edit">
            <div className="ff-back-link" onClick={store.backToIndex} role="button" tabIndex={0}>
                <i className="fal fa-arrow-left" />
                All feature flags
            </div>

            <div className="ff-edit__header">
                <h1>{flag.label}</h1>
                <div className="ff-edit__meta">
                    {flag.name}
                    {' '}
                    · feature_id
                    {' '}
                    {flag.id}
                    {' '}
                    · last edited
                    {' '}
                    {flag.updated}
                </div>
            </div>

            <div className="ff-panel ff-edit__feature">
                <h3>Feature</h3>
                <div className="ff-edit__grid">
                    <div className="ff-field">
                        <label>Display name</label>
                        <input value={flag.label} onChange={store.updateLabel} />
                        <p className="ff-field__hint">What admins see in the table. Rename it whenever a clearer phrase comes along.</p>
                    </div>

                    <div className="ff-field">
                        <label>Feature name</label>
                        {flag.id && (
                            <div className="ff-locked-field">
                                <span>{flag.name}</span>
                                <span className="ff-locked-field__badge">
                                    <i className="fal fa-lock" />
                                    Locked
                                </span>
                            </div>

                        )}
                        {!flag.id && (
                            <div className="ff-field">
                                <input value={flag.name} onChange={store.updateName} />
                            </div>
                        )}
                        <p className="ff-field__hint">Set once, when the feature was created. The code references this name, so it cannot be changed.</p>

                    </div>

                    <div className="ff-field">
                        <label>Scope</label>
                        <div className="ff-scope-options">
                            <ScopeOption
                                option={{ label: 'global', hint: 'Answered at login' }}
                                active={flag.scope === 'global'}
                                onClick={() => store.setScope('global')}
                            />
                        </div>
                    </div>

                    <div className="ff-field">
                        <label>Active</label>
                        <div className="ff-active-toggle">
                            <Switch
                                cssClass={`${flag.active ? 'is-on' : 'is-off'}`}
                                onToggle={store.toggleActive}
                                state={flag.active}
                            />
                            {flag.active
                                ? <span>On — evaluation is running</span>
                                : <span className="ff-active-toggle__off">Off — dark everywhere, right now</span>}
                        </div>
                        <p className="ff-field__hint">A suspend switch. Switching off hides the feature everywhere on this server, and leaves the rules below exactly as they are.</p>
                    </div>

                    <div className="ff-field ff-field--full">
                        <label>Who can reach it</label>
                        <textarea
                            rows={2}
                            value={flag.description}
                            onChange={store.updateDescription}
                            placeholder="Describe the rule below in plain English — this is what everyone sees in the feature flag table."
                        />
                        <p className="ff-field__hint">Written by hand for now. Keep it in step with the rules below.</p>
                    </div>
                </div>
            </div>

            <div className="ff-stage" key="global">
                <div className="ff-stage__header">
                    <span className="ff-stage__band">Stage 1 · Global row</span>
                    <span className="ff-stage__when">Evaluated at login, cached for the session</span>
                </div>
                <Gate
                    store={store}
                    gate={store.flag.data.role}
                    scope="role"
                    label={store.scopeLabel.role}
                    sublabel="One per user"
                    matchAnyOptions={store.userRoles}
                    matchAnyCallback={store.flag.toggleGlobalRole}
                    optionToLabel={store.roleToUser}
                    ignoreText="Any role reaches this feature, including report and director users."
                />
                <Gate
                    store={store}
                    gate={store.flag.data.addons}
                    scope="addons"
                    label={store.scopeLabel.addons}
                    sublabel="None, one or many · from permissions"
                    matchAnyOptions={[['isAdmin', 'Explori Admin'], ['isStaff', 'Explori Staff'], ['isReseller', 'Explori Reseller']]}
                    matchAnyCallback={store.flag.toggleGlobalAddon}
                    ignoreText="Staff, admin and reseller status make no difference here."
                />

                <Combinator
                    data={store.flag.data}
                />
            </div>

            <Savebar
                store={store}
            />
        </div>
    )
}

export default observer(EditView)
